/**
 * SmartAIController: the new AI as an engine Controller (AI-08). Live games and the benchmark both
 * use this class, so they run the same policy.
 *
 * It sees only its own TurnView, the public events the engine sends every controller, and the
 * showdowns. The baseline AI (ai.ts) is kept unchanged for comparison and rollback.
 */
import type { Action, Controller, HandRecord, PublicEvent, PublicTable, TurnView } from "../whiskey.ts";
import { AIController } from "../ai.ts";
import { BUDGETS, PROFILES, type Budget, type Profile, type ProfileName } from "./config.ts";
import { CardMemory } from "./memory.ts";
import { chooseAction, chooseKnock, type Diagnostics } from "./policy.ts";
import { chooseState } from "./stateChoice.ts";

export interface SmartOptions {
  seat: number;
  seed: number;
  profile?: ProfileName | Profile;
  budget?: Budget;
  /** Browser: false once this game is no longer the one on screen (stops stale thinking). */
  isCurrent?: () => boolean;
  onDiagnostics?: (d: Diagnostics) => void;
}

export interface SmartStats {
  decisions: number;
  latenciesMs: number[];
  timeouts: number;
  invalid: number;
  fallbacks: number;
  chosen: Record<string, number>;
}

const mix = (a: number, b: number): number => (Math.imul(a ^ 0x27d4eb2f, 0x165667b1) + Math.imul(b ^ 0x85ebca77, 0xc2b2ae3d)) >>> 0;

export class SmartAIController implements Controller {
  readonly profile: Profile;
  readonly memory: CardMemory;
  readonly stats: SmartStats = { decisions: 0, latenciesMs: [], timeouts: 0, invalid: 0, fallbacks: 0, chosen: {} };
  private readonly opts: SmartOptions;
  private readonly budget: Budget;
  private readonly fallback: AIController;
  private intendedKnock: boolean | null = null;
  private lastTable?: PublicTable;

  constructor(opts: SmartOptions) {
    this.opts = opts;
    this.profile = typeof opts.profile === "object" ? opts.profile : PROFILES[opts.profile ?? "observant"];
    this.budget = opts.budget ?? BUDGETS.live;
    this.memory = new CardMemory(opts.seat);
    this.fallback = new AIController(opts.seed);
  }

  private record(d: Diagnostics): void {
    this.stats.decisions++;
    this.stats.latenciesMs.push(d.elapsedMs);
    if (d.fallback === "timeout") this.stats.timeouts++;
    if (d.fallback === "baseline") this.stats.fallbacks++;
    this.opts.onDiagnostics?.(d);
  }

  async decideAction(view: TurnView): Promise<Action> {
    if (view.handNumber !== this.memory.handNumber) this.memory.newHand(view.handNumber);
    this.lastTable = view.table;
    const seed = mix(this.opts.seed, view.handNumber * 4096 + view.table.actionsThisHand * 2 + (view.middleRevealed ? 1 : 0));
    const { choice, diag } = await chooseAction(view, this.memory, this.profile, this.budget, seed, this.opts.isCurrent);
    this.record(diag);
    let action = choice.action;
    if (!view.legal.includes(action.type)) { this.stats.invalid++; action = this.fallback.decideAction(view); }
    const kind = action.type + (choice.knockAfter ? "+KNOCK" : "");
    this.stats.chosen[kind] = (this.stats.chosen[kind] ?? 0) + 1;
    // a swap or a face-up take leaves a hand we already know: keep the knock choice made with it.
    // After a blind draw the new card changes things, so that knock is decided afresh.
    this.intendedKnock = action.type === "SWAP_ONE" || (action.type === "TAKE_MIDDLE" && view.middleRevealed) ? choice.knockAfter : null;
    if (action.type === "BLIND") this.memory.pendingOwnDiscard = view.hand[Math.max(0, Math.min(4, action.give))];
    return action;
  }

  async decideKnock(view: TurnView): Promise<boolean> {
    if (this.intendedKnock !== null) { const k = this.intendedKnock; this.intendedKnock = null; return k && view.legal.includes("KNOCK"); }
    const seed = mix(this.opts.seed, view.handNumber * 4096 + view.table.actionsThisHand * 2 + 7777);
    const { choice, diag } = await chooseKnock(view, this.memory, this.profile, this.budget, seed, this.opts.isCurrent);
    if (diag.worldsUsed) this.record(diag);
    return choice;
  }

  onDeal(_hand: unknown, handNumber: number): void {
    this.memory.newHand(handNumber);
  }

  onObserve(e: PublicEvent): void {
    this.memory.observe(e);
    this.lastTable = e.table;
  }

  onHandEnd(r: HandRecord): void {
    this.memory.onHandEnd(r);
  }

  chooseState(unclaimed: string[], owned: string[], view: { handNumber: number; table?: PublicTable; handPoints?: number[] }): string {
    const table = view.table ?? this.lastTable;
    if (!table) return this.fallback.chooseState(unclaimed, owned);
    // the table's state lists are from before this award; ours must match `owned`
    const t: PublicTable = { ...table, unclaimed: [...unclaimed].sort(), players: table.players.map((p) => (p.seat === this.opts.seat ? { ...p, states: [...owned] } : p)) };
    return chooseState(this.opts.seat, t.unclaimed, t, this.memory.winRates(), view.handPoints ?? [0, 0, 0, 0, 0], mix(this.opts.seed, view.handNumber)).state;
  }

  chooseCapital(owned: string[]): string {
    return this.fallback.chooseCapital(owned);
  }
}
